/**
 * Prepaid coverage slice pieces — the prepaid-unit coverage records referenced
 * from billing accounts and invoices.
 *
 * Populated indirectly (no dedicated load case); read by id from the surfaces
 * that reference coverages.
 *
 * Contains:
 * - prepaidCoveragesInitialState / prepaidCoveragesReducers — adapter state +
 *   view setters
 * - prepaid coverage selectors, including the by-id-list lookups
 */
import { createSelector } from '@reduxjs/toolkit';

import { prepaidCoveragesAdapter } from '@sophocles/features/billing/slices/adapters';

export const prepaidCoveragesInitialState = prepaidCoveragesAdapter.getInitialState({
  view: {
    search: null,
    page: 0,
    order: 'asc',
    orderBy: 'id',
  },
});

export const prepaidCoveragesReducers = {
  setPrepaidCoveragePage(state, action) {
    state.prepaidCoverages.view.page = action.payload;
  },
  setPrepaidCoverageSearch(state, action) {
    state.prepaidCoverages.view.search = action.payload;
  },
  setPrepaidCoverageOrder(state, action) {
    state.prepaidCoverages.view.order = action.payload;
  },
  setPrepaidCoverageOrderBy(state, action) {
    state.prepaidCoverages.view.orderBy = action.payload;
  },
};

export const selectPrepaidCoverageEntities = state => {
  return state.billing.prepaidCoverages;
};

export const { selectById: selectPrepaidCoverageById } =
  prepaidCoveragesAdapter.getSelectors(selectPrepaidCoverageEntities);

export const selectPrepaidCoverage = id => state => selectPrepaidCoverageById(state, id);

export const selectPrepaidCoveragesById = ids => state => {
  if (!ids) return [];
  const idList = Array.isArray(ids) ? ids : [ids];
  return idList.map(id => selectPrepaidCoverageById(state, id)).filter(coverage => coverage != null);
};

export const selectPrepaidCoverageViewProperties = createSelector(selectPrepaidCoverageEntities, state => state.view);

export const selectAllPrepaidCoverages = createSelector(selectPrepaidCoverageEntities, state => {
  return Object.keys(state.entities).map(key => state.entities[key]);
});

export const selectPrepaidCoverages = ids => state => {
  return ids.map(id => selectPrepaidCoverageById(state, id));
};
